import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface MunicipioConfig {
  nome: string;
  estado: string;
  cnpj: string;
}

interface MetasConfig {
  mensal: number;
  anual: number;
}

interface JurosConfig {
  taxa_mensal: number;
  multa_atraso: number;
}

interface ParcelamentoConfig {
  parcela_minima: number;
  max_parcelas_avista: number;
  max_parcelas_parcelado: number;
}

interface DescontosConfig {
  avista: number;
  ate_12x: number;
  acima_12x: number;
}

export interface SystemConfig {
  municipio: MunicipioConfig;
  metas: MetasConfig;
  juros: JurosConfig;
  parcelamento: ParcelamentoConfig;
  descontos: DescontosConfig;
}

const defaultConfig: SystemConfig = {
  municipio: { nome: 'Município', estado: 'SP', cnpj: '' },
  metas: { mensal: 50000, anual: 600000 },
  juros: { taxa_mensal: 1, multa_atraso: 2 },
  parcelamento: { parcela_minima: 50, max_parcelas_avista: 1, max_parcelas_parcelado: 60 },
  descontos: { avista: 30, ate_12x: 20, acima_12x: 15 }
};

export const useSystemConfig = () => {
  return useQuery({
    queryKey: ['system-config'],
    queryFn: async (): Promise<SystemConfig> => {
      const { data, error } = await supabase
        .from('system_config')
        .select('config_key, config_value');
      
      if (error) {
        console.error('Erro ao buscar configurações:', error);
        return defaultConfig;
      }
      
      const config: SystemConfig = { ...defaultConfig };
      
      data?.forEach((item) => {
        const key = item.config_key as keyof SystemConfig;
        if (key in config) {
          config[key] = item.config_value as any;
        }
      });
      
      return config;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

export const useUpdateSystemConfig = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { error } = await supabase
        .from('system_config')
        .update({ config_value: value })
        .eq('config_key', key);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-config'] });
    },
  });
};

export const useMunicipioConfig = () => {
  const { data, isLoading } = useSystemConfig();
  return {
    municipio: data?.municipio ?? defaultConfig.municipio,
    isLoading,
  };
};

export const useMetasConfig = () => {
  const { data, isLoading } = useSystemConfig();
  return {
    metas: data?.metas ?? defaultConfig.metas,
    isLoading,
  };
};

export const useJurosConfig = () => {
  const { data, isLoading } = useSystemConfig();
  return {
    juros: data?.juros ?? defaultConfig.juros,
    isLoading,
  };
};

export const useDescontosConfig = () => {
  const { data, isLoading } = useSystemConfig();
  return {
    descontos: data?.descontos ?? defaultConfig.descontos,
    isLoading,
  };
};

export const useParcelamentoConfig = () => {
  const { data, isLoading } = useSystemConfig();
  return {
    parcelamento: data?.parcelamento ?? defaultConfig.parcelamento,
    isLoading,
  };
};
